import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ChipGroup from '../components/ChipGroup';
import PastorProfileView from '../components/PastorProfileView';
import PastorFiles from '../components/PastorFiles';
import { useVerifyCode } from '../components/VerifyCode';
import Loading from '../../../components/Loading';
import { pastorApi, errorMessage } from '../pastorApi';
import type { PastorFile, PastorProfile } from '../pastorApi';
import { usePastorMe } from '../usePastorMe';
import {
  AVAILABLE_FROM, CHURCH_SIZES, DENOMINATIONS, KEYWORD_MAX, NEEDS, PHILOSOPHY, REGION_TYPES, ROLES, RURAL_CALLING, SIDOS, STRENGTHS,
} from '../pastorOptions';

const EMPTY: PastorProfile = {
  realName: '', phone: '', email: '', birthYear: null, denomination: '', ordinationYear: null, ministryYears: null,
  currentRole: '', currentChurch: '', currentRegion: '', currentChurchSize: '',
  excludeChurches: [], hopeRegionTypes: [], hopeSidos: [], ruralCalling: '', churchSizePrefs: [], needs: [], needsNote: '',
  availableFrom: '', philosophyKeywords: [], philosophy: '', strengthKeywords: [], strengths: '', sermonUrls: [],
  education: '', career: '', familyNote: '', isActive: true,
};

const digits = (v: string) => v.replace(/[^\d]/g, '');
const lower = (v: string) => v.trim().toLowerCase();
const PHONE_RE = /^01\d-\d{3,4}-\d{4}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** '010-1234-5678' · '01012345678' → ['010', '1234', '5678'] */
function splitPhone(phone: string): [string, string, string] {
  if (phone.includes('-')) {
    const [a = '', b = '', c = ''] = phone.split('-');
    return [a, b, c];
  }
  const d = digits(phone);
  return d.length === 10 ? [d.slice(0, 3), d.slice(3, 6), d.slice(6)] : [d.slice(0, 3), d.slice(3, 7), d.slice(7)];
}

const numOrNull = (v: string) => (v.trim() === '' ? null : Number(v.replace(/[^\d]/g, '')) || null);

const PHILOSOPHY_PROMPTS = [
  '어떤 교회를 꿈꾸시나요?',
  '성도들과 어떤 관계를 맺고 싶으신가요?',
  '지방·작은 교회를 섬기는 일에 대해 어떻게 생각하시나요?',
];

// 사역자 전용 — 담임목회 희망 이력서 등록/수정 (사역자모아만 보관, 동의한 교회에만 전달)
export default function PastorProfileEdit() {
  const navigate = useNavigate();
  const { isLogin, loading, me, reload, isChurch } = usePastorMe();
  const [form, setForm] = useState<PastorProfile>(EMPTY);
  const [excludeInput, setExcludeInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState('');
  const [showFull, setShowFull] = useState(false);
  const [phoneParts, setPhoneParts] = useState<[string, string, string]>(['010', '', '']);
  const [verifiedPhone, setVerifiedPhone] = useState('');
  const [verifiedEmail, setVerifiedEmail] = useState('');
  const [files, setFiles] = useState<PastorFile[]>([]);

  useEffect(() => {
    if (me?.pastorProfile) {
      setForm({ ...EMPTY, ...me.pastorProfile });
      if (me.pastorProfile.phone) setPhoneParts(splitPhone(me.pastorProfile.phone));
      setFiles(me.pastorProfile.files || []);
    }
  }, [me]);

  // 저장된 번호·이메일을 그대로 두면 다시 인증하지 않는다
  const savedPhone = digits(me?.pastorProfile?.phone || '');
  const savedEmail = lower(me?.pastorProfile?.email || '');
  const phoneOk = (Boolean(savedPhone) && digits(form.phone) === savedPhone) || (Boolean(verifiedPhone) && digits(form.phone) === verifiedPhone);
  const emailOk = (Boolean(savedEmail) && lower(form.email) === savedEmail) || (Boolean(verifiedEmail) && lower(form.email) === verifiedEmail);
  const phoneVerify = useVerifyCode({ channel: 'phone', target: form.phone, name: form.realName.trim(), valid: PHONE_RE.test(form.phone), verified: phoneOk, onVerified: (t) => setVerifiedPhone(digits(t)) });
  const emailVerify = useVerifyCode({ channel: 'email', target: form.email.trim(), valid: EMAIL_RE.test(form.email.trim()), verified: emailOk, onVerified: (t) => setVerifiedEmail(lower(t)) });

  const setPhonePart = (i: number, value: string, max: number) => {
    const next = [...phoneParts] as [string, string, string];
    next[i] = digits(value).slice(0, max);
    setPhoneParts(next);
    setForm((f) => ({ ...f, phone: next.some(Boolean) ? next.join('-') : '' }));
    if (next[i].length === max && i < 2) document.getElementById(`pc-phone-${i + 1}`)?.focus();
  };

  const set = <K extends keyof PastorProfile>(key: K, value: PastorProfile[K]) => setForm((f) => ({ ...f, [key]: value }));
  const text = (key: keyof PastorProfile) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => set(key, e.target.value as never);

  if (!isLogin) {
    return (
      <div className="pastor-call inner pc-page">
        <div className="pc-gate">
          <p className="pc-gate__title">로그인 후 담임목회 희망 이력서를 등록할 수 있습니다</p>
          <p>맡겨 주신 이력서는 사역자모아만 보관하며, 목사님이 동의하신 교회에만 전달됩니다.</p>
          <button type="button" className="mm-btn mm-btn--primary pc-btn-lg" onClick={() => navigate('/login')}>로그인하기</button>
        </div>
      </div>
    );
  }
  if (loading) return <div className="pastor-call inner pc-page"><Loading /></div>;
  if (isChurch) {
    return (
      <div className="pastor-call inner pc-page">
        <div className="pc-gate">
          <p className="pc-gate__title">교회 담당자 계정입니다</p>
          <p>담임목회 희망 이력서는 사역자 회원만 등록할 수 있습니다. 담임목사님을 찾으시면 청빙 의뢰서를 보내 주세요. 사역자모아가 맞는 목사님을 찾아 이력서를 전달해 드립니다.</p>
          <button type="button" className="mm-btn mm-btn--primary pc-btn-lg" onClick={() => navigate('/pastor/request')}>청빙 의뢰하기</button>
        </div>
      </div>
    );
  }

  const addExclude = () => {
    const v = excludeInput.trim();
    if (!v || form.excludeChurches.includes(v) || form.excludeChurches.length >= 10) return;
    set('excludeChurches', [...form.excludeChurches, v]);
    setExcludeInput('');
  };

  const setSermon = (i: number, v: string) => {
    const next = [...form.sermonUrls];
    next[i] = v;
    set('sermonUrls', next);
  };

  const save = async () => {
    setError('');
    if (!phoneOk) { setError('휴대전화 인증을 완료해 주세요.'); return; }
    if (!emailOk) { setError('이메일 인증을 완료해 주세요.'); return; }
    setSaving(true);
    try {
      const res = await pastorApi.saveProfile({ ...form, sermonUrls: form.sermonUrls.map((u) => u.trim()).filter(Boolean) });
      setForm({ ...EMPTY, ...res.profile });
      setSavedAt(new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' }));
      reload();
    } catch (e) {
      setError(errorMessage(e, '저장하지 못했습니다.'));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async () => {
    const next = !form.isActive;
    try {
      await pastorApi.setActive(next);
      set('isActive', next);
    } catch (e) {
      alert(errorMessage(e));
    }
  };

  const remove = async () => {
    if (!window.confirm('이력서를 삭제할까요? 대기 중인 추천은 모두 종료되고, 이미 전달된 교회의 열람 링크도 닫힙니다. 되돌릴 수 없습니다.')) return;
    try {
      await pastorApi.deleteProfile();
      setForm(EMPTY);
      setFiles([]);
      reload();
    } catch (e) {
      alert(errorMessage(e));
    }
  };

  const registered = Boolean(me?.pastorProfile);

  return (
    <div className="pastor-call inner pc-page">
      <div className="pc-page__head">
        <h2>{registered ? '내 담임목회 희망 이력서' : '담임목회 희망 이력서 등록'}</h2>
        {registered && (
          <div className="pc-page__tools">
            <button type="button" className={`mm-btn ${form.isActive ? 'mm-btn--outline' : 'mm-btn--primary'} pc-btn-sm`} onClick={toggleActive}>
              {form.isActive ? '추천 받기 일시중지' : '추천 다시 받기'}
            </button>
            <button type="button" className="mm-btn mm-btn--outline pc-btn-sm" onClick={() => navigate('/pastor/my')}>내 청빙 소식</button>
          </div>
        )}
      </div>

      <div className="pc-private">
        <p className="pc-private__title">🔒 이력서는 사역자모아만 보관합니다 — 교회가 직접 찾아볼 수 없습니다</p>
        <ul>
          <li>담임목사를 구하는 교회가 있으면 사역자모아가 목사님께 <b>먼저 여쭙니다.</b></li>
          <li>목사님이 <b>동의하신 교회 한 곳에만</b> 이력서와 연락처가 전달됩니다. 거절하시면 교회는 목사님을 알 수 없습니다.</li>
          <li>담임목사 청빙 공고가 올라오면 조건에 맞춰 <b>청빙 소식</b>을 보내 드립니다.</li>
        </ul>
        {registered && !form.isActive && <p className="pc-private__paused">지금은 추천 받기를 멈춘 상태라 교회 추천과 청빙 소식이 가지 않습니다.</p>}
      </div>

      <div className="pc-edit pc-edit--full">
        <div className="pc-edit__form">
          {/* 1. 비공개 기본 정보 */}
          <section className="pc-fs">
            <div className="pc-fs__head">
              <h3>1. 기본 정보</h3>
              <span className="pc-lock">동의한 교회에만 전달</span>
            </div>
            <div className="pc-grid">
              <label className="pc-field"><span>이름 *</span><input className="mm-field" value={form.realName} onChange={text('realName')} /></label>
              <div className="pc-field pc-field--wide">
                <span>휴대전화 *</span>
                <div className="pc-inline">
                  <div className="pc-phone">
                    <input id="pc-phone-0" className="mm-field" value={phoneParts[0]} onChange={(e) => setPhonePart(0, e.target.value, 3)} inputMode="numeric" aria-label="휴대전화 앞자리" />
                    <span>-</span>
                    <input id="pc-phone-1" className="mm-field" value={phoneParts[1]} onChange={(e) => setPhonePart(1, e.target.value, 4)} inputMode="numeric" aria-label="휴대전화 가운데 자리" />
                    <span>-</span>
                    <input id="pc-phone-2" className="mm-field" value={phoneParts[2]} onChange={(e) => setPhonePart(2, e.target.value, 4)} inputMode="numeric" aria-label="휴대전화 끝자리" />
                  </div>
                  {phoneVerify.button}
                </div>
                {phoneVerify.row}
              </div>
              <div className="pc-field pc-field--wide">
                <span>이메일 *</span>
                <div className="pc-inline">
                  <input className="mm-field" value={form.email} onChange={text('email')} placeholder="추천 소식을 받을 이메일" inputMode="email" />
                  {emailVerify.button}
                </div>
                {emailVerify.row}
              </div>
            </div>
            <p className="pc-hint">휴대전화는 카카오 알림톡 인증번호로 확인합니다. 교회 추천과 청빙 소식은 앱 알림과 이메일로 보내 드립니다.</p>
          </section>

          {/* 2. 목회 이력 */}
          <section className="pc-fs">
            <div className="pc-fs__head"><h3>2. 목회 이력</h3></div>
            <div className="pc-grid">
              <label className="pc-field"><span>출생 연도</span><input className="mm-field" value={form.birthYear ?? ''} onChange={(e) => set('birthYear', numOrNull(e.target.value))} placeholder="예) 1975" inputMode="numeric" /></label>
              <label className="pc-field">
                <span>교단</span>
                <select className="mm-field" value={form.denomination} onChange={text('denomination')}>
                  <option value="">선택</option>
                  {DENOMINATIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </label>
              <label className="pc-field"><span>목사 안수 연도</span><input className="mm-field" value={form.ordinationYear ?? ''} onChange={(e) => set('ordinationYear', numOrNull(e.target.value))} placeholder="예) 2008" inputMode="numeric" /></label>
              <label className="pc-field"><span>총 사역 경력(년)</span><input className="mm-field" value={form.ministryYears ?? ''} onChange={(e) => set('ministryYears', numOrNull(e.target.value))} placeholder="예) 18" inputMode="numeric" /></label>
              <label className="pc-field">
                <span>현재 직분</span>
                <select className="mm-field" value={form.currentRole} onChange={text('currentRole')}>
                  <option value="">선택</option>
                  {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </label>
              <label className="pc-field"><span>현 사역지 교회명</span><input className="mm-field" value={form.currentChurch} onChange={text('currentChurch')} placeholder="추천 시 이 교회는 제외합니다" /></label>
              <label className="pc-field">
                <span>현 사역지 지역</span>
                <select className="mm-field" value={form.currentRegion} onChange={text('currentRegion')}>
                  <option value="">선택</option>
                  {SIDOS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label className="pc-field">
                <span>현 사역지 규모</span>
                <select className="mm-field" value={form.currentChurchSize} onChange={text('currentChurchSize')}>
                  <option value="">선택</option>
                  {CHURCH_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
            </div>
          </section>

          {/* 3. 제외할 교회 */}
          <section className="pc-fs">
            <div className="pc-fs__head"><h3>3. 이력서를 보내지 않을 교회</h3></div>
            <div className="pc-field">
              <span>제외할 교회 <em>이 교회들에는 추천하지 않습니다</em></span>
              <div className="pc-inline">
                <input
                  className="mm-field"
                  value={excludeInput}
                  onChange={(e) => setExcludeInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addExclude(); } }}
                  placeholder="현재 섬기는 교회, 같은 노회·지방회 교회 등"
                />
                <button type="button" className="mm-btn mm-btn--outline pc-btn-md" onClick={addExclude}>추가</button>
              </div>
              {form.excludeChurches.length > 0 && (
                <div className="pc-chips">
                  {form.excludeChurches.map((c) => (
                    <button key={c} type="button" className="mm-chip pc-chip" onClick={() => set('excludeChurches', form.excludeChurches.filter((x) => x !== c))}>
                      {c} ✕
                    </button>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* 4. 소명과 희망 사역지 */}
          <section className="pc-fs">
            <div className="pc-fs__head"><h3>4. 소명과 희망 사역지</h3></div>
            <div className="pc-field">
              <span>지방·농어촌 교회 사역에 대한 마음 *</span>
              <div className="pc-radio-cards">
                {RURAL_CALLING.map((r) => (
                  <button key={r.value} type="button" className={`pc-radio-card${form.ruralCalling === r.value ? ' on' : ''}`} onClick={() => set('ruralCalling', r.value)}>
                    <b>{r.label}</b>
                    <span>{r.desc}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="pc-field"><span>희망 지역 유형</span><ChipGroup options={REGION_TYPES} value={form.hopeRegionTypes} onChange={(v) => set('hopeRegionTypes', v)} /></div>
            <div className="pc-field"><span>희망 시·도 <em>선택하지 않으면 전국</em></span><ChipGroup options={SIDOS} value={form.hopeSidos} onChange={(v) => set('hopeSidos', v)} /></div>
            <div className="pc-field"><span>희망 교회 규모</span><ChipGroup options={CHURCH_SIZES} value={form.churchSizePrefs} onChange={(v) => set('churchSizePrefs', v)} /></div>
            <div className="pc-field"><span>부임 가능 시기</span><ChipGroup multi={false} options={AVAILABLE_FROM} value={form.availableFrom ? [form.availableFrom] : []} onChange={(v) => set('availableFrom', v[0] || '')} /></div>
          </section>

          {/* 5. 필요 조건 */}
          <section className="pc-fs">
            <div className="pc-fs__head"><h3>5. 필요 조건</h3><span className="pc-fs__sub">교회를 고를 때 참고합니다</span></div>
            <div className="pc-field"><span>꼭 필요한 조건</span><ChipGroup options={NEEDS} value={form.needs} onChange={(v) => set('needs', v)} /></div>
            <div className="pc-grid">
              <label className="pc-field pc-field--wide"><span>조건 메모</span><input className="mm-field" value={form.needsNote} onChange={text('needsNote')} placeholder="예) 자녀가 고등학생이라 통학 가능한 지역이면 좋겠습니다" /></label>
              <label className="pc-field pc-field--wide"><span>가족 사항</span><input className="mm-field" value={form.familyNote} onChange={text('familyNote')} placeholder="예) 사모, 자녀 2명 (사모는 반주 가능)" /></label>
            </div>
          </section>

          {/* 6. 목회 철학 · 강점 */}
          <section className="pc-fs">
            <div className="pc-fs__head"><h3>6. 목회 철학과 사역 강점</h3></div>
            <div className="pc-field"><span>목회 키워드 <em>최대 {KEYWORD_MAX}개</em></span><ChipGroup options={PHILOSOPHY} value={form.philosophyKeywords} onChange={(v) => set('philosophyKeywords', v)} max={KEYWORD_MAX} /></div>
            <label className="pc-field">
              <span>목회 철학 * <em>{form.philosophy.trim().length}자 · 20자 이상</em></span>
              <textarea className="mm-field" rows={7} value={form.philosophy} onChange={text('philosophy')} placeholder={PHILOSOPHY_PROMPTS.map((q) => `· ${q}`).join('\n')} />
              <small className="pc-hint">교회가 이력서에서 가장 먼저 읽는 부분입니다. 학력·경력보다 목사님의 마음이 전해지는 글이 좋습니다.</small>
            </label>
            <div className="pc-field"><span>사역 강점 <em>최대 {KEYWORD_MAX}개</em></span><ChipGroup options={STRENGTHS} value={form.strengthKeywords} onChange={(v) => set('strengthKeywords', v)} max={KEYWORD_MAX} /></div>
            <label className="pc-field"><span>강점 설명</span><textarea className="mm-field" rows={4} value={form.strengths} onChange={text('strengths')} placeholder="예) 15년간 시골 교회에서 어르신 심방과 마을 섬김을 해 왔습니다." /></label>
          </section>

          {/* 7. 설교 영상 */}
          <section className="pc-fs">
            <div className="pc-fs__head"><h3>7. 설교 영상</h3><span className="pc-fs__sub">선택 · 최대 3개</span></div>
            {[0, 1, 2].map((i) => (
              <label key={i} className="pc-field">
                <span>유튜브 주소 {i + 1}</span>
                <input className="mm-field" value={form.sermonUrls[i] || ''} onChange={(e) => setSermon(i, e.target.value)} placeholder="https://youtu.be/..." />
              </label>
            ))}
            <p className="pc-hint">'일부 공개' 영상도 주소만 있으면 교회가 볼 수 있습니다.</p>
          </section>

          {/* 8. 학력·경력 */}
          <section className="pc-fs">
            <div className="pc-fs__head"><h3>8. 학력·경력 요약</h3><span className="pc-fs__sub">선택</span></div>
            <label className="pc-field"><span>학력</span><input className="mm-field" value={form.education} onChange={text('education')} placeholder="예) 신학대학원 M.Div 졸업" /></label>
            <label className="pc-field"><span>사역 경력</span><textarea className="mm-field" rows={4} value={form.career} onChange={text('career')} placeholder="예) 경북 지역 농촌교회 담임 8년, 수도권 중형교회 부목사 6년" /></label>
          </section>

          {/* 9. 구직 서류 */}
          <section className="pc-fs">
            <div className="pc-fs__head">
              <h3>9. 구직 서류</h3>
              <span className="pc-fs__sub">자기소개서·추천서 등 · 선택</span>
              <span className="pc-lock">동의한 교회에만 전달</span>
            </div>
            {registered ? (
              <PastorFiles files={files} onChange={setFiles} />
            ) : (
              <p className="pc-hint">이력서를 사역자모아에 맡기신 뒤 자기소개서 등 서류를 올려 보관할 수 있습니다.</p>
            )}
          </section>

          {error && <p className="pc-error">{error}</p>}
          <div className="pc-savebar">
            {savedAt && <span className="pc-savebar__ok">{savedAt} 저장됨</span>}
            {registered && <button type="button" className="mm-btn mm-btn--ghost" onClick={remove}>이력서 삭제</button>}
            <button type="button" className="mm-btn mm-btn--outline pc-btn-lg" onClick={() => setShowFull(true)}>미리보기</button>
            <button type="button" className="mm-btn mm-btn--primary pc-btn-lg" disabled={saving} onClick={save}>
              {saving ? '저장 중…' : registered ? '변경 내용 저장' : '사역자모아에 이력서 맡기기'}
            </button>
          </div>
        </div>
      </div>

      {showFull && (
        <div className="pc-modal-overlay" onClick={() => setShowFull(false)}>
          <div className="pc-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="pc-modal__head">
              <h3>{form.realName || '이름'} 목사님 이력서 미리보기</h3>
              <button type="button" className="pc-modal__close" onClick={() => setShowFull(false)} aria-label="닫기">✕</button>
            </div>
            <div className="pc-modal__body">
              <PastorProfileView
                profile={{ ...form, files, sermonUrls: form.sermonUrls.filter(Boolean) }}
                onOpenFile={(f) => pastorApi.downloadFile(f).catch((e) => alert(errorMessage(e, '내려받지 못했습니다.')))}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
